import { useId, useState } from "react";

import { usePinnedChatSaveTarget } from "~/ai-chat/feature/use-chat-save-target";
import { useSaveResourceAction } from "~/ai-chat/feature/use-save-resource-action";
import { chatSaveWarnings, describeSaveDestination } from "~/ai-chat/model/chat-save";
import type { SaveResourceAction as SaveResourceActionModel } from "~/ai-chat/model/save-resource-action";
import {
  ChatSaveResourceCard,
  type SaveResourceCardChoices,
  type SaveResourceCardState,
} from "~/ai-chat/ui/chat-save-resource-card";
import { useGetPlatformInfosecSettings } from "~/api/useInfosecLevels";
import { ControlTagMultiSelect } from "~/control-tags/feature/control-tag-multi-select";
import { useAllowedInfosecLevels, useInfosecFeatureEnabled } from "~/hooks";
import { buildResourcePath } from "~/routes";

interface ChatSaveResourceActionProps {
  action: SaveResourceActionModel;
}

export function ChatSaveResourceAction({ action }: ChatSaveResourceActionProps) {
  const controlTagsId = useId();
  const [cardState, setCardState] = useState<SaveResourceCardState>({ status: "ready" });
  const [controlTagIds, setControlTagIds] = useState<string[]>([]);
  const target = usePinnedChatSaveTarget();
  const destination = target.status === "ready" ? target : undefined;
  const { save } = useSaveResourceAction(destination);
  const featureEnabled = useInfosecFeatureEnabled();
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  const infosecLevels = useAllowedInfosecLevels(undefined, { enabled: featureEnabled });

  // Off feature shell ⇒ definitively false so Save is never blocked; on an infosec
  // deployment `undefined` keeps Save blocked until the platform setting resolves.
  const infosecEnabled = featureEnabled ? platformSettingsQuery.data?.enabled : false;
  const isLocked = cardState.status === "saving" || cardState.status === "saved";

  const handleSave = async (edited: SaveResourceActionModel, choices: SaveResourceCardChoices) => {
    setCardState({ status: "saving" });
    try {
      const saved = await save(edited, { ...choices, controlTagIds });
      const warnings = chatSaveWarnings(saved, destination?.systemName);
      setCardState({
        status: "saved",
        link: { type: "model", path: buildResourcePath({ resourceId: saved.resourceId }) },
        destination: saved.placedInSystem && destination ? describeSaveDestination(destination) : undefined,
        warning: warnings.length > 0 ? warnings.join(" ") : undefined,
      });
    } catch {
      setCardState({ status: "error", message: "Could not save the resource. Please try again." });
    }
  };

  return (
    <ChatSaveResourceCard
      action={action}
      state={cardState}
      infosecEnabled={infosecEnabled}
      infosecLevels={infosecLevels}
      destination={
        target.status === "loading"
          ? undefined
          : destination
            ? { systemName: destination.systemName, branchName: destination.branch.name }
            : null
      }
      controlTagsField={
        <ControlTagMultiSelect
          id={controlTagsId}
          label={<span className="text-xs">Control tags</span>}
          value={controlTagIds}
          onChange={setControlTagIds}
          disabled={isLocked}
          compact
        />
      }
      onSave={(edited, choices) => void handleSave(edited, choices)}
    />
  );
}
